import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, IntlProvider } from 'react-intl';

import TestResultsHeader from '../../../components/TestResultsHeader';
import ReportBox from '../../../components/ReportBox';
import Divider from '../../../components/Divider';
import TestResultsNavigation from '../TestResultsNavigation';
import TestResultsDocuments from '../TestResultsDocuments';
import LongevityCheckInfoCardListSelection from '../LongevityCheckInfoCardListSelection';
import TextWithTextActionAccordionListCard from '../../../components/TextWithTextActionAccordionListCard';
import TextBannerSmall from '../../../components/TextBannerSmall';
import BannerStyleCard from '../../../components/BannerStyleCard';

import useScrollHandler from './useScrollHandler.hook';
import formatDate from '../../helpers/dateFormat';
import englishMessages from '../../../translations/en/common.json';

import styles from './index.module.scss';

const TestResultsLongevityCheck = ({ data, language }) => {
  const {
    title,
    description,
    overallResult,
    documents,
    infoBlock,
    resultsAvailableDate,
    longevityCheckResults,
    reports,
    treatment,
    infoBannerNoFrench,
  } = data;
  const { wellbeingScores, priorityActions } = longevityCheckResults;

  const { scrollableElementRef, linksData, activeSection } = useScrollHandler({
    data,
  });

  const { labReport, reimbursementReceipt } = documents;

  const { interactiveReport, additionalActions } = infoBlock;

  const isAnyDocumentsAvailable = labReport || reimbursementReceipt;

  const formattedResultsAvailableDate = formatDate(resultsAvailableDate);

  const pageContent = (
    <div className={styles.contentWrapper}>
      <div className={styles.content}>
        {infoBannerNoFrench && (
          <div className={styles.infoBlock}>
            <TextBannerSmall
              title={infoBannerNoFrench.data.title}
              backgroundColor={infoBannerNoFrench.data.backgroundColor}
            />
          </div>
        )}
        <TestResultsHeader
          className={styles.testResultsHeader}
          title={title}
          overallResult={overallResult}
          resultsAvailableDate={formattedResultsAvailableDate}
        />
        <div className={styles.navigationMobileWrapper}>
          <TestResultsNavigation
            className={styles.navigationMobile}
            linksData={linksData}
            activeSection={activeSection}
            scrollableElementRef={scrollableElementRef}
          />
        </div>
        {description && !reports?.doctorReport && (
          <div id='summary' className={styles.summary}>
            <Divider
              title={
                <FormattedMessage id='app.summary' defaultMessage='Summary' />
              }
            />
            <ReportBox className={styles.reportBox} text={description} />
          </div>
        )}

        {reports?.doctorReport && (
          <div id='doctor' className={styles.doctorReport}>
            <Divider
              title={
                <FormattedMessage
                  id='app.doctors-report'
                  defaultMessage="Doctor's Report"
                />
              }
            />
            <ReportBox
              className={styles.reportBox}
              text={reports.doctorReport.reportText}
              name={reports.doctorReport.writtenBy}
            />
          </div>
        )}

        <div id='wellbeingScores' className={styles.wellbeingScores}>
          <Divider
            className={styles.divider}
            title={
              <FormattedMessage
                id='test-result.wellbeing-scores'
                defaultMessage='Wellbeing Scores'
              />
            }
          />
          <LongevityCheckInfoCardListSelection
            className={styles.infoCardListSelection}
            description={wellbeingScores.description}
            wellbeingScores={wellbeingScores}
          />
        </div>

        {infoBlock && interactiveReport && (
          <div className={styles.infoBlock}>
            <TextBannerSmall
              title={interactiveReport.title}
              text={interactiveReport.text}
              buttonUrl={interactiveReport.url}
              backgroundColor={interactiveReport.color}
            />
          </div>
        )}

        {longevityCheckResults && priorityActions && (
          <div id='priorityActions' className={styles.priorityActions}>
            <Divider
              className={styles.divider}
              title={
                <FormattedMessage
                  id='test-result.priority-actions'
                  defaultMessage='Priority Actions'
                />
              }
            />
            <TextWithTextActionAccordionListCard
              className={styles.textWithTextActionAccordionListCard}
              text={priorityActions.description}
              accordions={priorityActions.actions}
            />
          </div>
        )}

        {infoBlock && additionalActions && (
          <div className={styles.infoBlock}>
            <TextBannerSmall
              title={additionalActions.title}
              text={additionalActions.text}
              buttonUrl={additionalActions.url}
              backgroundColor={additionalActions.color}
            />
          </div>
        )}

        {treatment && treatment.upsellCard && (
          <div id='upsellCard' className={styles.upsellCard}>
            <BannerStyleCard
              textBlockItems={treatment.upsellCard}
              listItems={treatment.upsellCard.listItems}
              description={treatment.upsellCard.description}
              buttonText={treatment.upsellCard.buttonText}
              buttonUrl={treatment.upsellCard.buttonUrl}
              backgroundColor={treatment.upsellCard.color}
            />
          </div>
        )}

        {isAnyDocumentsAvailable && (
          <div className={styles.documentsWrapperMobile}>
            <TestResultsDocuments documents={documents} />
          </div>
        )}
      </div>
      <div className={styles.sidebarWrapper}>
        <div className={styles.sidebar}>
          <TestResultsNavigation
            className={styles.navigationDesktop}
            linksData={linksData}
            activeSection={activeSection}
          />
          <TestResultsDocuments documents={documents} />
        </div>
      </div>
    </div>
  );

  return (
    <div ref={scrollableElementRef} className={styles.wrapper}>
      {infoBannerNoFrench ? (
        <IntlProvider locale='en' messages={englishMessages}>
          {pageContent}
        </IntlProvider>
      ) : (
        pageContent
      )}
    </div>
  );
};

TestResultsLongevityCheck.propTypes = {
  data: PropTypes.object,
  language: PropTypes.string,
};

export default TestResultsLongevityCheck;
